import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Widgets from "../../../../widgets";
import strings from "../../../../utils/strings";
import Icons from "../../../../Assets/Icons";
import "./AddNewZoneFee.css";

const stripCommas = (v) => String(v || "").replace(/,/g, "");

const formatWithCommas = (raw) => {
  const onlyDigits = String(raw || "").replace(/\D/g, "");
  if (!onlyDigits) return "";
  return onlyDigits.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

const CurrencyInput = ({ name, value, onChange, placeholder = "0", disabled = false }) => {
  return (
    <div className="znf-money">
      <span>₫</span>
      <input type="text" name={name} value={value} placeholder={placeholder} onChange={onChange} autoComplete="off" disabled={disabled} />
    </div>
  );
};

const AddNewZoneFee = () => {
  const { t } = useTranslation();
  const nav = useNavigate();
  const location = useLocation();

  const zoneFromState = location.state?.zone || null;

  const breadcrumbs = ["nursingCare", "transportationFee", "zoneFeeDetails"];
  const paths = [strings.navigation.manageNurseReq, strings.navigation.manageTranspotationFees, location.pathname];

  const [zoneName, setZoneName] = useState(zoneFromState?.name || "");

  const [motoSame, setMotoSame] = useState(true);
  const [taxiSame, setTaxiSame] = useState(true);

  const [motoWorking, setMotoWorking] = useState({ intl: "", local: "", subsidized: "" });
  const [motoNonWorking, setMotoNonWorking] = useState({ intl: "", local: "", subsidized: "" });

  const [taxiWorking, setTaxiWorking] = useState({ intl: "", local: "", subsidized: "" });
  const [taxiNonWorking, setTaxiNonWorking] = useState({ intl: "", local: "", subsidized: "" });

  const isValid = useMemo(() => zoneName.trim().length > 0, [zoneName]);

  const handleZoneName = (e) => {
    setZoneName(e.target.value.trimStart().slice(0, 80));
  };

  const handleMoneyChange = (setter) => (e) => {
    const { name, value } = e.target;
    setter((prev) => ({ ...prev, [name]: formatWithCommas(value) }));
  };

  const toggleSameAsWorking = (mode) => {
    if (mode === "motobike") {
      setMotoSame((prev) => {
        const next = !prev;
        if (next) setMotoNonWorking(motoWorking);
        return next;
      });
    } else {
      setTaxiSame((prev) => {
        const next = !prev;
        if (next) setTaxiNonWorking(taxiWorking);
        return next;
      });
    }
  };

  const handleSubmit = () => {
    if (!isValid) return;
    const payload = {
      zoneName: zoneName.trim(),
      motobike: {
        working: { ...motoWorking, intl: stripCommas(motoWorking.intl), local: stripCommas(motoWorking.local), subsidized: stripCommas(motoWorking.subsidized) },
        nonWorking: motoSame
          ? { ...motoWorking, intl: stripCommas(motoWorking.intl), local: stripCommas(motoWorking.local), subsidized: stripCommas(motoWorking.subsidized) }
          : { ...motoNonWorking, intl: stripCommas(motoNonWorking.intl), local: stripCommas(motoNonWorking.local), subsidized: stripCommas(motoNonWorking.subsidized) },
      },
      taxi: {
        working: { ...taxiWorking, intl: stripCommas(taxiWorking.intl), local: stripCommas(taxiWorking.local), subsidized: stripCommas(taxiWorking.subsidized) },
        nonWorking: taxiSame
          ? { ...taxiWorking, intl: stripCommas(taxiWorking.intl), local: stripCommas(taxiWorking.local), subsidized: stripCommas(taxiWorking.subsidized) }
          : { ...taxiNonWorking, intl: stripCommas(taxiNonWorking.intl), local: stripCommas(taxiNonWorking.local), subsidized: stripCommas(taxiNonWorking.subsidized) },
      },
    };

    // UI-only: API wiring can be added later using existing thunk patterns.
    nav(strings.navigation.manageTranspotationFees, { state: { saved: true, payload, mode: "create" } });
  };

  return (
    <div className="mr-container">
      <Widgets.BreadCrumb breadcrumbs={breadcrumbs} paths={paths} />

      <div className="znf-heading">{t("addNewZoneFee")}</div>

      <div className="znf-card">
        <div className="znf-zone-field">
          <label>
            {t("zoneName")} <span className="asterik">*</span>
          </label>
          <input type="text" className="znf-money" value={zoneName} onChange={handleZoneName} placeholder={t("enterZoneName")} />
        </div>

        <div className="znf-section-title">{t("setFeesForEachTransportMode")}</div>

        {/* Motobike */}
        <div className="znf-mode">
          <div className="znf-mode-header">
            <div className="znf-mode-name">
              <img src={Icons.Location} alt="" />
              <span>{t("motobikeFees")}</span>
            </div>
          </div>

          <div className="whp-container znf-block">
            <div className="title-hr">
              <p>{t("workingHoursLabel")}</p>
              <hr />
            </div>
            <div className="service-type-main2">
              <div className="service-input-box">
                <span>{t("opdInternationalInsured")}</span>
                <CurrencyInput name="intl" value={motoWorking.intl} onChange={handleMoneyChange(setMotoWorking)} />
              </div>
              <div className="service-input-box">
                <span>{t("opdLocalInsured")}</span>
                <CurrencyInput name="local" value={motoWorking.local} onChange={handleMoneyChange(setMotoWorking)} />
              </div>
              <div className="service-input-box">
                <span>{t("opdSubsidized")}</span>
                <CurrencyInput name="subsidized" value={motoWorking.subsidized} onChange={handleMoneyChange(setMotoWorking)} />
              </div>
            </div>
          </div>

<div className="whp-container znf-block">
  <div className="title-hr2">
    <p>{t("nonWorkingHoursLabel")}</p>
    <hr />
  </div>

  <button
    type="button"
    className={`clickable-text znf-same-link ${motoSame ? "active" : ""}`}
    onClick={() => toggleSameAsWorking("motobike")}
  >
    {t("sameAsWorkingHours")}
  </button>

  <div className="service-type-main2">
    <div className="service-input-box">
      <span>{t("opdInternationalInsured")}</span>
      <CurrencyInput
        name="intl"
        value={motoSame ? motoWorking.intl : motoNonWorking.intl}
        onChange={handleMoneyChange(setMotoNonWorking)}
        disabled={motoSame}
      />
    </div>

    <div className="service-input-box">
      <span>{t("opdLocalInsured")}</span>
      <CurrencyInput
        name="local"
        value={motoSame ? motoWorking.local : motoNonWorking.local}
        onChange={handleMoneyChange(setMotoNonWorking)}
        disabled={motoSame}
      />
    </div>

    <div className="service-input-box">
      <span>{t("opdSubsidized")}</span>
      <CurrencyInput
        name="subsidized"
        value={motoSame ? motoWorking.subsidized : motoNonWorking.subsidized}
        onChange={handleMoneyChange(setMotoNonWorking)}
        disabled={motoSame}
      />
    </div>
  </div>
</div>
        </div>

        {/* Taxi */}
        <div className="znf-mode">
          <div className="znf-mode-header">
            <div className="znf-mode-name">
              <img src={Icons.Location} alt="" />
              <span>{t("taxiFees")}</span>
            </div>
          </div>

          <div className="whp-container znf-block">
            <div className="title-hr">
              <p>{t("workingHoursLabel")}</p>
              <hr />
            </div>
            <div className="service-type-main2">
              <div className="service-input-box">
                <span>{t("opdInternationalInsured")}</span>
                <CurrencyInput name="intl" value={taxiWorking.intl} onChange={handleMoneyChange(setTaxiWorking)} />
              </div>
              <div className="service-input-box">
                <span>{t("opdLocalInsured")}</span>
                <CurrencyInput name="local" value={taxiWorking.local} onChange={handleMoneyChange(setTaxiWorking)} />
              </div>
              <div className="service-input-box">
                <span>{t("opdSubsidized")}</span>
                <CurrencyInput name="subsidized" value={taxiWorking.subsidized} onChange={handleMoneyChange(setTaxiWorking)} />
              </div>
            </div>
          </div>

<div className="whp-container znf-block">
  <div className="title-hr2">
    <p>{t("nonWorkingHoursLabel")}</p>
    <hr />
  </div>

  <button
    type="button"
    className={`clickable-text znf-same-link ${taxiSame ? "active" : ""}`}
    onClick={() => toggleSameAsWorking("taxi")}
  >
    {t("sameAsWorkingHours")}
  </button>

  <div className="service-type-main2">
    <div className="service-input-box">
      <span>{t("opdInternationalInsured")}</span>
      <CurrencyInput
        name="intl"
        value={taxiSame ? taxiWorking.intl : taxiNonWorking.intl}
        onChange={handleMoneyChange(setTaxiNonWorking)}
        disabled={taxiSame}
      />
    </div>

    <div className="service-input-box">
      <span>{t("opdLocalInsured")}</span>
      <CurrencyInput
        name="local"
        value={taxiSame ? taxiWorking.local : taxiNonWorking.local}
        onChange={handleMoneyChange(setTaxiNonWorking)}
        disabled={taxiSame}
      />
    </div>

    <div className="service-input-box">
      <span>{t("opdSubsidized")}</span>
      <CurrencyInput
        name="subsidized"
        value={taxiSame ? taxiWorking.subsidized : taxiNonWorking.subsidized}
        onChange={handleMoneyChange(setTaxiNonWorking)}
        disabled={taxiSame}
      />
    </div>
  </div>
</div>
        </div>

        <div className="save-speciality znf-actions">
          <button type="button" className="form-section-cancel" onClick={() => nav(strings.navigation.manageTranspotationFees)}>
            {t("cancel")}
          </button>
          <button
            type="button"
            className={`form-section-create ${!isValid ? "znf-disabled" : ""}`}
            onClick={handleSubmit}
            disabled={!isValid}
          >
            {t("createEntry")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddNewZoneFee;

